import React, { useState, useEffect, useCallback } from 'react'

import { useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { Loading, EmptyPageContent, PageHeader } from 'components/shared'
import { ContactsImageSources } from './ImageSources'
import { ContactsList } from './components/ContactsList'
import { getContacts, toggleKeypadModalVisible, requestContactStart, requestContactFinish } from 'store/actions/global'
import PhoneKeypad from 'components/pages/dialpad/components/PhoneKeypad'
import PhoneInput from 'components/pages/dialpad/components/PhoneInput'
import { useDebounce } from 'use-debounce'
import { useTranslation } from 'react-i18next'
import './Contacts.scss'

export function Contacts({
    modalSlug = null,
    backHandler,
    scrollContainerRef,
    onContactClick = () => {},
    isHeaderVisible = true,
    inputHandler = true,
    inputExternalValue = '',
    inputValueProp = '',
    contactClickFromListHandler = true,
    isPhoneKeypadForNoContacts = false,
    addInputValue,
    onBackspace,
    isRequest = true
}) {
    const [inputValue, setInputValue] = useState('')
    const [contactHandler, setContactHandler] = useState(true)
    const [requestValue, setRequestValue] = useState('')

    const dispatch = useDispatch()
    const { slug } = useParams()
    const [debouncedValue] = useDebounce(inputValue, 1200)

    const data = useSelector((state) => state.global.contactsListData)
    const filteredData = useSelector((state) => state.global.contactsFilteredListData)
    const pagination = useSelector((state) => state.global.contactsListPagination)
    const loadingFilteredContacts = useSelector((state) => state.global.loadingContacts)
    const { loading, page, totalPages } = pagination
    const { calls } = useSelector((state) => state.call)
    let dataContactsHandler = filteredData.length ? filteredData : data

    function toggleKeypad() {
        dispatch(toggleKeypadModalVisible())
    }
    const resetInputValue = () => {
        setInputValue('')
        setContactHandler(true)
        dispatch(requestContactFinish())
    }
    const fillInputValue = (value) => setInputValue(value || '')

    useEffect(() => {
        if (inputValue.length) {
            dispatch(requestContactStart())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue])

    useEffect(() => {
        const value = inputHandler ? debouncedValue : inputExternalValue
        setRequestValue(value)
    }, [inputHandler, debouncedValue, inputExternalValue])

    useEffect(() => {
        const currentCallNumber = calls.filter((item) => item.number === requestValue)
        !currentCallNumber.length &&
            contactClickFromListHandler &&
            isRequest &&
            dispatch(getContacts({ params: { page: 1 }, requestValue }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestValue, calls, contactClickFromListHandler, isRequest])

    useEffect(() => {
        if (filteredData.length && requestValue.length) {
            setContactHandler(true)
        } else if (!filteredData.length && requestValue.length && contactClickFromListHandler) {
            setContactHandler(false)
        } else {
            setContactHandler(true)
        }
    }, [filteredData, requestValue, contactClickFromListHandler])

    useEffect(() => {
        if (scrollContainerRef?.current?.recalculate && typeof scrollContainerRef.current.recalculate === 'function') {
            scrollContainerRef.current.recalculate()
        }
    }, [scrollContainerRef, data])

    const onScroll = useCallback(() => {
        const scrollEl = scrollContainerRef?.current?.getScrollElement()
        if (scrollEl.offsetHeight + scrollEl.scrollTop >= scrollEl.scrollHeight) {
            if (!loading && page < totalPages && contactHandler) {
                dispatch(getContacts({ params: { page: page + 1 } }))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollContainerRef, totalPages, page, loading])

    useEffect(() => {
        const scrollEl = scrollContainerRef?.current?.getScrollElement()
        scrollEl && scrollEl.addEventListener('scroll', onScroll)
        return () => {
            scrollEl && scrollEl.removeEventListener('scroll', onScroll)
        }
    }, [scrollContainerRef, onScroll])

    const { t } = useTranslation()

    return (
        <div className="contacts-page">
            {!modalSlug && <PageHeader pageName={t('navigation.contacts')} />}

            {inputHandler && (
                <PhoneInput
                    value={inputValue}
                    onChange={fillInputValue}
                    onReset={resetInputValue}
                    onStickyInput={true}
                    helpClass="dialpad__input-holder--contact"
                    placeholder={t('search_contact')}
                />
            )}

            {!!dataContactsHandler?.length &&
                !loadingFilteredContacts &&
                (contactHandler ? (
                    <ContactsList
                        contactsList={dataContactsHandler}
                        slug={modalSlug || slug}
                        toggleKeypad={toggleKeypad}
                        backHandler={backHandler}
                        onContactClick={onContactClick}
                        isHeaderVisible={isHeaderVisible}
                        searchValue={debouncedValue}
                        filteredContacts={filteredData}
                        addTransferInputValue={inputValueProp}
                    />
                ) : isPhoneKeypadForNoContacts ? (
                    <PhoneKeypad addInputValue={addInputValue} onBackspace={onBackspace} />
                ) : (
                    <EmptyPageContent
                        imgSrc={ContactsImageSources.contactEmptyImage}
                        messageLine1={t('no_results')}
                        bgImageSrc={ContactsImageSources.contactEmptyBgImage}
                        uiHandler={inputExternalValue}
                    />
                ))}

            {loading || loadingFilteredContacts ? (
                <Loading />
            ) : (
                !data?.length && (
                    <EmptyPageContent
                        imgSrc={ContactsImageSources.contactEmptyImage}
                        messageLine1={t('no_contacts')}
                        bgImageSrc={ContactsImageSources.contactEmptyBgImage}
                    />
                )
            )}
        </div>
    )
}
